// frontend/src/portal/pages/PortalTenantMembers.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPortalTenantMembers, updatePortalTenantMemberRole } from "../../api.js";
import { usePortalAuth } from "../components/RequireClient.jsx";
import ErrorBoundary from "../../components/ErrorBoundary.jsx";

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "reviewer", label: "Reviewer" },
  { value: "staff", label: "Staff" },
];

function formatSeatSummary(seatUsage, seats) {
  if (!seatUsage) {
    if (seats == null) return null;
    return `Seats: ${seats}`;
  }
  if (seatUsage.total == null) {
    return `Seats used: ${seatUsage.used ?? "--"} / Unlimited`;
  }
  return `Seats used: ${seatUsage.used ?? "--"} / ${seatUsage.total}`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

export default function PortalTenantMembers() {
  const { permissions, tenantSlug, tenant, refresh, memberRole } = usePortalAuth();
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seatUsage, setSeatUsage] = useState(null);
  const [savingMemberId, setSavingMemberId] = useState(null);
  const [success, setSuccess] = useState(null);

  const effectiveRole = memberRole || "client_user";
  const isSuperAdmin = effectiveRole === "super_admin";
  const canManageUsers = useMemo(() => isSuperAdmin || (Array.isArray(permissions) && permissions.includes("users.manage")), [isSuperAdmin, permissions]);

  const seatSummary = useMemo(() => formatSeatSummary(seatUsage, tenant?.seats ?? null), [seatUsage, tenant?.seats]);

  const loadMembers = useCallback(async () => {
    if (!tenantSlug || !canManageUsers) {
      setLoading(false);
      setMembers([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetchPortalTenantMembers(tenantSlug);
      setMembers(Array.isArray(response?.members) ? response.members : []);
      setSeatUsage(response?.seatUsage || null);
    } catch (err) {
      setError(err);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, canManageUsers]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const errorText = useMemo(() => {
    if (!error) return null;
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message;
    return error?.message || "Unable to load members.";
  }, [error]);

  if (!canManageUsers) {
    return (
      <ErrorBoundary componentName="PortalTenantMembers">
        <div style={{ minHeight: "100vh", background: "var(--bg-secondary)", padding: "var(--space-2xl)" }}>
          <div style={{ maxWidth: "var(--container-max)", margin: "0 auto" }}>
            <div
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-2xl)",
                textAlign: "center",
                border: "1px solid var(--border-primary)",
              }}>
              <h1 style={{ fontSize: "24px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "var(--space-sm)" }}>Access Denied</h1>
              <p style={{ color: "var(--text-secondary)", margin: 0 }}>You do not have permission to manage users.</p>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  const handleRoleChange = async (memberId, roleValue) => {
    if (!tenantSlug || !roleValue) return;
    setSavingMemberId(memberId);
    setError(null);
    setSuccess(null);
    try {
      const response = await updatePortalTenantMemberRole(tenantSlug, memberId, roleValue);
      const updatedMember = response?.member || null;
      const nextSeatUsage = response?.seatUsage || null;
      setMembers((prev) => {
        if (!updatedMember) return prev;
        return prev.map((entry) => (entry.userId === updatedMember.userId ? updatedMember : entry));
      });
      setSeatUsage(nextSeatUsage);
      setSuccess("Role updated successfully.");
      await refresh().catch(() => {});
    } catch (err) {
      if (err?.status === 409 && err?.data?.code === "SEAT_LIMIT") {
        setError(
          err?.data?.seatUsage?.remaining === 0 ? new Error("Seat limit reached. Remove an active member before upgrading roles.") : new Error("Seat limit prevents this change.")
        );
      } else {
        setError(err);
      }
    } finally {
      setSavingMemberId(null);
    }
  };

  return (
    <ErrorBoundary componentName="PortalTenantMembers">
      <div style={{ minHeight: "100vh", background: "var(--bg-secondary)" }}>
        {/* Header */}
        <div style={{ background: "var(--accent-gradient)", borderBottom: "1px solid var(--border-secondary)" }}>
          <div style={{ maxWidth: "var(--container-max)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h1 style={{ fontSize: "32px", fontWeight: "400", color: "#fff", margin: "0 0 var(--space-sm) 0", letterSpacing: "-0.5px" }}>Manage Members</h1>
                {seatSummary && <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.9)", margin: 0 }}>{seatSummary}</p>}
              </div>

              {memberRole === "super_admin" && !tenant?.clerkOrgId && (
                <button
                  onClick={() => navigate("/portal/admin/invite")}
                  style={{
                    padding: "var(--space-sm) var(--space-lg)",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.3)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}>
                  Link Organization
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: "var(--container-max)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
          {errorText && (
            <div
              style={{
                backgroundColor: "var(--danger-bg)",
                border: "1px solid var(--danger)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-lg)",
                marginBottom: "var(--space-xl)",
              }}>
              <p style={{ color: "var(--danger)", margin: 0, fontSize: "14px" }}>{errorText}</p>
            </div>
          )}

          {success && (
            <div
              style={{
                backgroundColor: "var(--success-bg)",
                border: "1px solid var(--success)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-lg)",
                marginBottom: "var(--space-xl)",
              }}>
              <p style={{ color: "var(--success)", margin: 0, fontSize: "14px", fontWeight: "500" }}>✓ {success}</p>
            </div>
          )}

          {/* Members Table */}
          <div
            style={{
              backgroundColor: "var(--surface)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border-primary)",
              overflow: "hidden",
              boxShadow: "var(--shadow-sm)",
            }}>
            <div style={{ padding: "var(--space-xl)", borderBottom: "1px solid var(--border-primary)" }}>
              <h2 style={{ fontSize: "18px", fontWeight: "600", color: "var(--text-primary)", margin: 0 }}>Team Members</h2>
            </div>

            {loading ? (
              <div style={{ padding: "var(--space-2xl)", textAlign: "center" }}>
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    border: "3px solid var(--border-primary)",
                    borderTopColor: "var(--accent-primary)",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                    margin: "0 auto var(--space-lg)",
                  }}
                />
                <p style={{ color: "var(--text-muted)" }}>Loading members…</p>
              </div>
            ) : members.length === 0 ? (
              <div style={{ padding: "var(--space-2xl)", textAlign: "center" }}>
                <p style={{ color: "var(--text-muted)", margin: 0 }}>No members found.</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "var(--surface-elevated)", borderBottom: "1px solid var(--border-primary)" }}>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Name</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Email & Dates</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => {
                      const currentRole = member.role || "";
                      const memberName = member.firstName || member.lastName ? `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim() : null;
                      const createdAt = formatDateTime(member.userCreatedAt);
                      const updatedAt = formatDateTime(member.userUpdatedAt);

                      return (
                        <tr key={member.userId} style={{ borderBottom: "1px solid var(--border-primary)" }}>
                          <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-primary)", fontWeight: "500" }}>{memberName || "—"}</td>
                          <td style={{ padding: "var(--space-md)" }}>
                            <div style={{ fontSize: "14px", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>{member.email || "—"}</div>
                            <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                              Created {createdAt} • Updated {updatedAt}
                            </div>
                          </td>
                          <td style={{ padding: "var(--space-md)" }}>
                            <select
                              value={currentRole}
                              onChange={(e) => handleRoleChange(member.userId, e.target.value)}
                              disabled={savingMemberId === member.userId}
                              style={{
                                padding: "var(--space-sm) var(--space-md)",
                                border: "1px solid var(--border-primary)",
                                borderRadius: "var(--radius-md)",
                                fontSize: "14px",
                                backgroundColor: "var(--surface)",
                                color: "var(--text-primary)",
                                cursor: savingMemberId === member.userId ? "not-allowed" : "pointer",
                              }}>
                              {ROLE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </ErrorBoundary>
  );
}
